export interface DepartmentNode {
  id: string | number;
  name: string;
  parent_id?: string | number | null;
}

export const ELLIPSIS_NODE = { id: 'ellipsis', name: '...' } as const;

/**
 * The department and its ancestors, root first. Stops at a parent that isn't
 * in `allDepartments` (e.g. the list hasn't loaded) and at a repeated id, so a
 * bad parent loop can't hang the render.
 */
export const getDepartmentPath = <T extends DepartmentNode>(
  department: T | null | undefined,
  allDepartments: T[] = []
): T[] => {
  if (!department) return [];

  const byId = new Map(allDepartments.map((item) => [item.id, item]));
  const path: T[] = [department];
  const seen = new Set([department.id]);
  let current: T | undefined = department;

  while (
    current?.parent_id !== null &&
    current?.parent_id !== undefined &&
    !seen.has(current.parent_id)
  ) {
    const parent = byId.get(current.parent_id);
    if (!parent) break;
    path.unshift(parent);
    seen.add(parent.id);
    current = parent;
  }

  return path;
};

/** Up to three levels in full; deeper paths keep the root and the last two. */
export const collapseDepartmentPath = <T extends DepartmentNode>(
  path: T[]
): Array<T | typeof ELLIPSIS_NODE> =>
  path.length <= 3 ? path : [path[0], ELLIPSIS_NODE, ...path.slice(-2)];

const PATH_SEPARATOR = ' › ';

/**
 * A department as text for surfaces that can't render the breadcrumb (chart
 * axes): the collapsed path to show and the full path for a tooltip. Found by
 * name when exactly one department has it; otherwise the name stands alone.
 */
export const departmentPathText = (
  name: string,
  allDepartments: DepartmentNode[] = []
): { collapsed: string; full: string } => {
  const named = allDepartments.filter((item) => item.name === name);
  if (named.length !== 1) return { collapsed: name, full: name };

  const path = getDepartmentPath(named[0], allDepartments);
  return {
    collapsed: collapseDepartmentPath(path)
      .map((node) => node.name)
      .join(PATH_SEPARATOR),
    full: path.map((node) => node.name).join(PATH_SEPARATOR),
  };
};
